import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, RefreshCw, RotateCw } from "lucide-react";
import api from "@/lib/api";
import { rp } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";

export default function Payment() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("pending");
  const [checking, setChecking] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [split, setSplit] = useState(null); // { total_splits, paid_splits, current_split }

  const applyStatus = useCallback((data, notify = false) => {
    if (data.split) {
      setSplit({ total_splits: data.total_splits, paid_splits: data.paid_splits,
        current_split: data.current_split });
    }
    if (data.payment_status === "paid") {
      setStatus("paid");
      if (notify) toast.success("Pembayaran diterima!");
      setTimeout(() => navigate("/pesanan"), 1500);
      return true;
    }
    return false;
  }, [navigate]);

  const fetchStatus = useCallback(async () => {
    const { data } = await api.get(`/orders/${orderId}/payment-status`);
    return data;
  }, [orderId]);

  const checkNow = async () => {
    setChecking(true);
    try {
      const data = await fetchStatus();
      const paid = applyStatus(data, true);
      if (!paid) {
        if (data.split) toast.info(`Terbayar ${data.paid_splits} dari ${data.total_splits} split. Selesaikan split berikutnya.`);
        else toast.info(`Pembayaran belum terkonfirmasi${data.provider_status ? ` — status: ${data.provider_status}` : ""}. Coba lagi setelah membayar.`);
      }
    } catch { toast.error("Gagal mengecek transaksi. Coba lagi."); }
    finally { setChecking(false); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/regenerate-qr`);
      setSplit((s) => ({ ...(s || {}), current_split: data.current_split }));
      toast.success("QR baru dibuat. Silakan scan ulang.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat QR baru."); }
    finally { setRegenerating(false); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data);
      if (data.payment_status === "paid") { setStatus("paid"); return; }
      // Untuk split, ambil QR aktif dari payment-status
      if (data.split_payment) {
        try { applyStatus(await fetchStatus()); } catch {}
      }
    } catch {}
  }, [orderId, fetchStatus, applyStatus]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (status === "paid") return;
    const iv = setInterval(async () => {
      try { if (applyStatus(await fetchStatus())) clearInterval(iv); } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [status, fetchStatus, applyStatus]);

  const isSplit = !!(order?.split_payment || split);
  const current = split?.current_split;
  const singleInfo = order?.payment_info || {};
  const qr = isSplit ? current?.qr_content : singleInfo.qr_content;
  const payUrl = isSplit ? current?.pay_url : singleInfo.pay_url;
  const qrImg = qr && /^https?:\/\//.test(qr) ? qr
    : (qr ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(qr)}` : null);
  const activeAmount = isSplit ? current?.amount : order?.amount;

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-10 text-center">
        <h1 className="font-serif text-2xl font-bold text-rl-ink">Pembayaran QRIS</h1>
        <p className="text-sm text-rl-muted mt-1">{order?.order_number} · {rp(order?.amount)}</p>

        {isSplit && (
          <div className="mt-3" data-testid="split-progress">
            <p className="text-sm font-semibold text-rl-ink">
              Terbayar {split?.paid_splits ?? 0} dari {split?.total_splits ?? order?.total_splits}
            </p>
            <div className="mt-2 flex items-center justify-center gap-2">
              {Array.from({ length: split?.total_splits || order?.total_splits || 0 }).map((_, i) => {
                const done = i < (split?.paid_splits ?? 0);
                const active = i === (split?.paid_splits ?? 0) && status !== "paid";
                return (
                  <div key={i} data-testid={`split-step-${i + 1}`}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border transition-colors ${
                      done ? "bg-emerald-500 border-emerald-500 text-white"
                      : active ? "bg-rl-primary border-rl-primary text-white"
                      : "bg-white border-rl-border text-rl-muted"}`}>
                    {done ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-rl-muted">
              QRIS Indonesia dibatasi Rp10jt/transaksi, jadi pembayaran dipecah menjadi beberapa QR berurutan.
            </p>
          </div>
        )}

        {status === "paid" ? (
          <div className="mt-10" data-testid="payment-success">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
            <p className="mt-4 font-semibold text-rl-ink">Pembayaran berhasil!</p>
            <button onClick={() => navigate("/pesanan")} className="mt-6 rounded-full bg-rl-primary text-white font-bold px-6 py-3">Lihat Pesanan</button>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-rl-border bg-white p-6">
            {isSplit && current && (
              <p className="mb-3 text-sm font-semibold text-rl-ink" data-testid="current-split-label">
                Split {current.index} · Bayar {rp(activeAmount)}
              </p>
            )}
            {qrImg ? <img src={qrImg} data-testid="qris-image" alt="QRIS" className="w-64 h-64 mx-auto rounded-lg border border-rl-border" />
              : <div className="text-rl-muted py-10">Menyiapkan kode pembayaran...</div>}
            {payUrl && <a href={payUrl} target="_blank" rel="noreferrer" data-testid="pay-url"
              className="mt-4 inline-block rounded-full bg-rl-secondary text-white font-bold px-6 py-2.5">Buka Halaman Pembayaran</a>}
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-rl-muted"><Loader2 className="w-4 h-4 animate-spin" />Menunggu pembayaran...</div>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <button data-testid="check-transaction" onClick={checkNow} disabled={checking}
                className="inline-flex items-center gap-2 rounded-full border border-rl-border hover:border-rl-primary text-rl-ink font-semibold px-6 py-2.5 text-sm transition-colors disabled:opacity-50">
                <RefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} />{checking ? "Mengecek..." : "Cek Transaksi"}</button>
              {isSplit && (
                <button data-testid="regenerate-qr" onClick={regenerate} disabled={regenerating}
                  className="inline-flex items-center gap-2 rounded-full border border-rl-border hover:border-rl-primary text-rl-ink font-semibold px-6 py-2.5 text-sm transition-colors disabled:opacity-50">
                  <RotateCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />{regenerating ? "Membuat..." : "Buat Ulang QR"}</button>
              )}
            </div>
            <p className="mt-3 text-xs text-rl-muted">Scan QRIS dengan aplikasi bank / e-wallet, lalu klik "Cek Transaksi" jika status belum berubah.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
